import React, { useState, useEffect } from 'react';
import { Box, Button, LinearProgress, Sheet, Typography, Alert, Link } from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { ThemeStep } from './ThemeStep';
import { CompanyInfoStep, type CompanyInfo } from './CompanyInfoStep';
import { DomainStep } from './DomainStep';
import { MarketingPhotosStep } from './MarketingPhotosStep';
import { PagesStep } from './PagesStep';
import type { PageOption } from './PageTreeItem';
import { api } from '../../../lib/api';
import { DomainManager } from '../domains';
import PreviewSiteButton from '../PreviewSiteButton';

interface SelectedImage {
  url: string;
  file?: File;
  alt?: string;
  productionPhotoId?: number;  // ID of production photo to import
}

export const OnboardingWizard: React.FC = () => {
  const [step, setStep] = useState(0);
  const totalSteps = 5;
  const [theme, setTheme] = useState('');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo>({
    name: '',
    phone: '',
    email: '',
    address: '',
    areasServed: [],
  });
  const [domain, setDomain] = useState('');
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [saving, setSaving] = useState(false);
  const [createdSite, setCreatedSite] = useState<{id: number; domain: string; is_active: boolean} | null>(null);

  // Check if site already exists
  const { data: existingSites, isLoading: checkingExistingSites } = useQuery({
    queryKey: ['cms-sites-check'],
    queryFn: async () => {
      const response = await api.get<any>('/api/cms/sites/');
      const sites = (response.data?.results || response.data || []);
      return sites;
    },
  });

  // Redirect to existing site if one exists
  useEffect(() => {
    if (!checkingExistingSites && existingSites && existingSites.length > 0) {
      const existingSite = existingSites[0];
      window.location.href = `/cms/sites/${existingSite.id}/pages`;
    }
  }, [checkingExistingSites, existingSites]);

  const [pages, setPages] = useState<PageOption[]>([
    { 
      slug: 'index', 
      title: 'Home', 
      checked: true,
      order: 0
    },
    { 
      slug: 'residential-roofing', 
      title: 'Residential Roofing', 
      checked: true,
      order: 1,
      children: [
        { slug: 'residential-roof-replacement', title: 'Residential Roof Replacement', checked: true, order: 0, parent: 'residential-roofing' },
        { slug: 'residential-roof-repairs', title: 'Residential Roof Repairs', checked: true, order: 1, parent: 'residential-roofing' },
        { slug: 'residential-roof-installation', title: 'Residential Roof Installation', checked: true, order: 2, parent: 'residential-roofing' },
        { slug: 'storm-and-hail-damage', title: 'Storm and Hail Damage', checked: true, order: 3, parent: 'residential-roofing' },
      ]
    },
    { 
      slug: 'commercial-roofing', 
      title: 'Commercial Roofing', 
      checked: true,
      order: 2,
      children: [
        { slug: 'commercial-roof-replacement', title: 'Commercial Roof Replacement', checked: true, order: 0, parent: 'commercial-roofing' },
        { slug: 'commercial-roof-repairs', title: 'Commercial Roof Repairs', checked: true, order: 1, parent: 'commercial-roofing' },
        { slug: 'commercial-roof-installation', title: 'Commercial Roof Installation', checked: true, order: 2, parent: 'commercial-roofing' },
        { slug: 'commercial-roof-maintenance', title: 'Commercial Roof Maintenance', checked: true, order: 3, parent: 'commercial-roofing' },
      ]
    },
    { 
      slug: 'additional-services', 
      title: 'Additional Services', 
      checked: true,
      order: 3,
      children: [
        { slug: 'gutter-installation', title: 'Gutter Installation', checked: true, order: 0, parent: 'additional-services' },
      ]
    },
    { slug: 'about-us', title: 'About Us', checked: true, order: 4 },
    { slug: 'faqs', title: 'FAQs', checked: true, order: 5 },
    { slug: 'blog', title: 'Blog', checked: true, order: 6 },
    { slug: 'contact', title: 'Contact', checked: true, order: 7 },
  ]);


  const progress = (step / (totalSteps - 1)) * 100;

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleFinish = async () => {
    setSaving(true);
    try {
      const siteRes = await api.post('/api/cms/sites/', {
        theme_key: theme,
        name: companyInfo.name || 'My Website',
        domain: domain,
        settings: {
          company: companyInfo,
        }
      });
      const siteId = (siteRes.data as {id: number})?.id;
      const siteDomain = (siteRes.data as {domain?: string})?.domain || domain;

      // Create a map to store page IDs by slug for parent references
      const pageIdMap: Record<string, number> = {};
      
      // Function to recursively create pages
      const createPages = async (pageList: PageOption[], parentId: number | null = null) => {
        for (const page of pageList) {
          if (page.checked) {
            const pageData: any = {
              site: siteId,  // Add the site field
              slug: page.slug,
              title: page.title,
              order: page.order || 0,
            };
            
            if (parentId) {
              pageData.parent = parentId;
            }
            
            const pageRes = await api.post(`/api/cms/sites/${siteId}/pages/`, pageData);
            const createdPageId = (pageRes.data as {id: number})?.id;
            pageIdMap[page.slug] = createdPageId;
            
            // Create children if they exist
            if (page.children && page.children.length > 0) {
              await createPages(page.children, createdPageId);
            }
          }
        }
      };
      
      // Create all pages with proper hierarchy
      await createPages(pages);

      // Create location pages for areas served
      if (companyInfo.areasServed && companyInfo.areasServed.length > 0) {
        // Helper to convert area names to URL-friendly slugs
        const slugify = (str: string) =>
          str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');

        // Create parent "locations" page
        const locationsParentRes = await api.post(`/api/cms/sites/${siteId}/pages/`, {
          site: siteId,
          slug: 'locations',
          title: 'Locations',
          order: pages.length,
        });
        const locationsParentId = (locationsParentRes.data as { id: number })?.id;

        // Create a page for each served area
        for (const [idx, area] of companyInfo.areasServed.entries()) {
          const areaSlug = slugify(area);
          const locationPageRes = await api.post(`/api/cms/sites/${siteId}/pages/`, {
            site: siteId,
            slug: areaSlug,
            title: area,
            parent: locationsParentId,
            order: idx,
          });
          const locationPageId = (locationPageRes.data as { id: number })?.id;

          // Generate starter copy for this location page
          await api.post('/api/cms/ai/generate-site-starter-copy/', {
            site: siteId,
            page: locationPageId,
          });
        }
      }

      await api.post('/api/cms/ai/generate-site-starter-copy/', { site: siteId });

      // Import production photos first
      const productionPhotoIds = images
        .filter(img => img.productionPhotoId)
        .map(img => img.productionPhotoId as number);
      
      if (productionPhotoIds.length > 0) {
        await api.post('/api/cms/media/import-from-production/', {
          site: siteId,
          photo_ids: productionPhotoIds
        });
      }

      // Then upload new files
      for (const img of images) {
        if (img.file) {
          const form = new FormData();
          form.append('file', img.file);
          form.append('site', String(siteId));
          form.append('alt_text', img.alt || '');
          await api.post('/api/cms/media/upload/', form);
        }
      }
      
      const siteActive = (siteRes.data as { is_active?: boolean })?.is_active ?? false;
      // Show success screen with links
      setCreatedSite({ id: siteId, domain: siteDomain, is_active: siteActive });
    } catch (error) {
      console.error('Failed to create site:', error);
      alert('Failed to create site. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return <ThemeStep value={theme} onChange={setTheme} />;
      case 1:
        return <CompanyInfoStep value={companyInfo} onChange={setCompanyInfo} />;
      case 2:
        return (
          <>
            <Alert color="neutral" variant="soft" sx={{ mb: 2 }}>
              Use a temporary subdomain to preview your site or connect your own
              custom domain. Custom domains require DNS configuration. See the{' '}
              <Link href="#domain-manager">Domain Manager</Link> for detailed steps.
            </Alert>
            <DomainStep value={domain} onChange={setDomain} />
          </>
        );
      case 3:
        return <PagesStep pages={pages} onChange={setPages} />;
      case 4:
        return (
          <MarketingPhotosStep siteId={null} images={images} onChange={setImages} />
        );
      default:
        return null;
    }
  };

  // Show loading while checking for existing sites
  if (checkingExistingSites) {
    return (
      <Sheet variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography level="body-lg">Checking for existing site...</Typography>
        </Box>
      </Sheet>
    );
  }

  // Show alert if site already exists (shouldn't reach here due to redirect, but just in case)
  if (existingSites && existingSites.length > 0) {
    return (
      <Sheet variant="outlined" sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert color="warning">
          Your organization already has a website. Redirecting to your site...
        </Alert>
      </Sheet>
    );
  }

  if (createdSite) {
    return (
      <Sheet variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography level="h4" gutterBottom>
            Site Created!
          </Typography>
          <Typography level="body-md" mb={3}>
            Your site has been created successfully. Add a domain below to go live.
          </Typography>
          <Box id="domain-manager">
            <DomainManager siteId={createdSite.id} />
          </Box>
          <Box display="flex" gap={2} justifyContent="center" mt={3}>
            <PreviewSiteButton siteId={createdSite.id} domain={createdSite.domain} isActive={createdSite.is_active} />
            <Button
              variant="outlined"
              component="a"
              href={`/cms/sites/${createdSite.id}/pages`}
            >
              Skip for now
            </Button>
          </Box>
        </Box>
      </Sheet>
    );
  }

  return (
    <Sheet variant="outlined" sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
      <Typography level="body-sm" mb={1}>
        Step {step + 1} of {totalSteps}
      </Typography>
      <LinearProgress determinate value={progress} sx={{ mb: 3 }} />
      {renderStep()}
      <Box
        mt={3}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Button
          disabled={step === 0 || saving}
          onClick={back}
          variant="outlined"
          size="lg"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Back
        </Button>
        {step < totalSteps - 1 && (
          <Button
            onClick={next}
            disabled={step === 2 && !domain}
            size="lg"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Next
          </Button>
        )}
        {step === totalSteps - 1 && (
          <Button
            onClick={handleFinish}
            loading={saving}
            variant="solid"
            size="lg"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create My Site
          </Button>
        )}
      </Box>
    </Sheet>
  );
};

export default OnboardingWizard;
